// =============================================================================
// Webinar Module — Zustand Store
// =============================================================================
import { create } from 'zustand';

const useWebinarStore = create((set, get) => ({
  // Live session state
  webinarId: null,
  webinar: null,
  participants: [],
  messages: [],
  myRole: 'participant', // host | co-host | moderator | participant

  // Media state
  isMuted: true,
  isCameraOn: false,
  isScreenSharing: false,
  screenSharer: null,

  // UI state
  activePanel: 'chat', // chat | qna | participants | polls
  isPanelOpen: true,
  raisedHands: [],
  reactions: [], // ephemeral reactions for animation
  activePoll: null,

  // Socket reference
  socket: null,

  // Actions
  setWebinar: (webinar) => set({ webinar, webinarId: webinar?.id }),
  setMyRole: (role) => set({ myRole: role }),
  setSocket: (socket) => set({ socket }),

  // Participants — supports both direct value and callback (prev => newVal) style
  setParticipants: (participantsOrFn) => set((state) => ({
    participants: typeof participantsOrFn === 'function'
      ? participantsOrFn(state.participants)
      : participantsOrFn,
  })),

  // Messages
  addMessage: (msg) => set((s) => ({
    messages: [...s.messages, msg].slice(-500) // keep last 500
  })),
  setMessages: (messages) => set({ messages }),
  removeMessage: (msgId) => set((s) => ({
    messages: s.messages.filter(m => m.id !== msgId)
  })),
  pinMessage: (msgId, isPinned) => set((s) => ({
    messages: s.messages.map(m => m.id === msgId ? { ...m, is_pinned: isPinned } : m)
  })),

  // Media
  toggleMute: () => {
    const { isMuted, socket, webinarId } = get();
    const newState = !isMuted;
    set({ isMuted: newState });
    socket?.emit('webinar:mute', { is_muted: newState });
  },
  toggleCamera: () => {
    const { isCameraOn, socket } = get();
    const newState = !isCameraOn;
    set({ isCameraOn: newState });
    socket?.emit('webinar:camera', { is_camera_on: newState });
  },
  setScreenSharing: (isSharing) => set({ isScreenSharing: isSharing }),
  setScreenSharer: (userId) => set({ screenSharer: userId }),

  // Hand raise
  setRaisedHands: (hands) => set({ raisedHands: hands }),
  toggleHandRaise: () => {
    const { socket } = get();
    socket?.emit('webinar:hand-raise');
  },

  // Reactions
  addReaction: (reaction) => set((s) => ({
    reactions: [...s.reactions, { ...reaction, _key: Date.now() + Math.random() }]
  })),
  clearReaction: (key) => set((s) => ({
    reactions: s.reactions.filter(r => r._key !== key)
  })),
  sendReaction: (emoji) => {
    const { socket } = get();
    socket?.emit('webinar:reaction', { emoji });
  },

  // Polls
  setActivePoll: (poll) => set({ activePoll: poll }),

  // Panel
  setActivePanel: (panel) => set({ activePanel: panel }),
  togglePanel: () => set((s) => ({ isPanelOpen: !s.isPanelOpen })),

  // Reset
  reset: () => set({
    webinarId: null,
    webinar: null,
    participants: [],
    messages: [],
    myRole: 'participant',
    isMuted: true,
    isCameraOn: false,
    isScreenSharing: false,
    screenSharer: null,
    activePanel: 'chat',
    isPanelOpen: true,
    raisedHands: [],
    reactions: [],
    activePoll: null,
    socket: null,
  }),
}));

export default useWebinarStore;
